import type { ProjectSamenvatting, Taak, ExportItem, Capaciteiten, ProjectModel } from '@video-studio/shared';

/**
 * API-client.
 *
 * Alle fouten komen hier als ApiFoutException binnen, met de Nederlandse
 * melding van de server. De UI hoeft daardoor nooit een statuscode te
 * interpreteren.
 */

export class ApiFoutException extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiFoutException';
  }
}

async function verzoek<T>(
  pad: string,
  opties: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = opties;
  const respons = await fetch(pad, {
    ...rest,
    headers: {
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(rest.headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (respons.status === 204) return undefined as T;

  const tekst = await respons.text();
  let data: unknown = null;
  if (tekst) {
    try {
      data = JSON.parse(tekst);
    } catch {
      data = { ruw: tekst };
    }
  }

  if (!respons.ok) {
    const fout = (data as { fout?: { code?: string; melding?: string; details?: unknown } })?.fout;
    const uitzondering = new ApiFoutException(
      respons.status,
      fout?.code ?? 'ONBEKEND',
      fout?.melding ?? `De server gaf status ${respons.status}.`,
      fout?.details,
    );
    if (uitzondering.status === 401 && uitzondering.code === 'NIET_INGELOGD') {
      bijSessieVerlopen?.();
    }
    throw uitzondering;
  }
  return data as T;
}

export interface ProjectDto {
  id: string;
  naam: string;
  versie: number;
  schemaVersie: number;
  fps: number;
  breedte: number;
  hoogte: number;
  duurInFrames: number;
  gemaaktOp: string;
  gewijzigdOp: string;
  project: ProjectModel;
}

export interface MediaDto {
  id: string;
  naam: string;
  soort: 'video' | 'afbeelding' | 'audio';
  mimeType: string;
  grootteBytes: number;
  inspectieStatus: 'wacht' | 'bezig' | 'klaar' | 'mislukt';
  inspectieFout: string | null;
  inspectie: {
    container: string;
    duurSeconden: number | null;
    breedte: number | null;
    hoogte: number | null;
    fps: number | null;
    videocodec: string | null;
    audiocodec: string | null;
    heeftAudio: boolean;
    rotatie: number;
    waarschuwingen: string[];
    ondersteund?: boolean;
  } | null;
  heeftProxy: boolean;
  bronUrl: string;
  proxyUrl: string | null;
  gemaaktOp: string;
}

let bijSessieVerlopen: (() => void) | null = null;

/** De app meldt zich als een verzoek 401 geeft, zodat het inlogscherm terugkomt. */
export function volgSessie(fn: () => void): void {
  bijSessieVerlopen = fn;
}

export const api = {
  sessie: () => verzoek<{ verplicht: boolean; ingelogd: boolean }>('/v1/auth/sessie'),
  login: (gebruikersnaam: string, wachtwoord: string) =>
    verzoek<{ ok: boolean }>('/v1/auth/login', { method: 'POST', json: { gebruikersnaam, wachtwoord } }),
  uitloggen: () => verzoek<{ ok: boolean }>('/v1/auth/uitloggen', { method: 'POST', json: {} }),

  capaciteiten: () => verzoek<Capaciteiten>('/v1/systeem/capaciteiten'),
  gezondheid: () => verzoek<{ ok: boolean }>('/v1/systeem/gezondheid'),

  projecten: () => verzoek<{ items: ProjectSamenvatting[] }>('/v1/projecten'),
  project: (id: string) => verzoek<ProjectDto>(`/v1/projecten/${id}`),
  maakProject: (invoer: { naam?: string; beeldformaat?: string }) =>
    verzoek<ProjectDto>('/v1/projecten', { method: 'POST', json: invoer }),
  bewaarProject: (
    id: string,
    project: ProjectModel,
    verwachteVersie: number,
    forceer = false,
  ) =>
    verzoek<ProjectDto>(`/v1/projecten/${id}`, {
      method: 'PUT',
      json: { project, verwachteVersie, forceer },
    }),
  verwijderProject: (id: string) => verzoek<void>(`/v1/projecten/${id}`, { method: 'DELETE' }),

  media: () => verzoek<{ items: MediaDto[] }>('/v1/media'),
  mediaItem: (id: string) => verzoek<MediaDto>(`/v1/media/${id}`),
  verwijderMedia: (id: string) => verzoek<void>(`/v1/media/${id}`, { method: 'DELETE' }),
  maakProxy: (id: string) =>
    verzoek<{ taakId: string }>(`/v1/media/${id}/proxy`, { method: 'POST', json: {} }),
  devVoorbeeld: () =>
    verzoek<{ taakId: string }>('/v1/media/dev-voorbeeld', { method: 'POST', json: {} }),

  uploadMedia: (bestand: File, soort: 'video' | 'afbeelding' | 'audio', onVoortgang?: (fractie: number) => void) =>
    new Promise<{ asset: MediaDto; alAanwezig: boolean; melding?: string }>((resolve, reject) => {
      const formulier = new FormData();
      formulier.append('soort', soort);
      formulier.append('bestand', bestand);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/v1/media/upload');
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onVoortgang?.(e.loaded / e.total);
      });
      xhr.addEventListener('load', () => {
        let data: unknown = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data as { asset: MediaDto; alAanwezig: boolean; melding?: string });
          return;
        }
        const fout = (data as { fout?: { code?: string; melding?: string } })?.fout;
        reject(new ApiFoutException(xhr.status, fout?.code ?? 'UPLOAD', fout?.melding ?? 'Upload mislukt.'));
      });
      xhr.addEventListener('error', () => reject(new ApiFoutException(0, 'NETWERK', 'De upload kon de server niet bereiken.')));
      xhr.send(formulier);
    }),

  taak: (id: string) => verzoek<Taak>(`/v1/taken/${id}`),
  taken: (query: { projectId?: string; status?: string; limiet?: number } = {}) => {
    const params = new URLSearchParams();
    if (query.projectId) params.set('projectId', query.projectId);
    if (query.status) params.set('status', query.status);
    if (query.limiet) params.set('limiet', String(query.limiet));
    const qs = params.toString();
    return verzoek<{ items: Taak[] }>(`/v1/taken${qs ? `?${qs}` : ''}`);
  },

  exports: (projectId: string) => verzoek<{ items: ExportItem[] }>(`/v1/projecten/${projectId}/exports`),
  startExport: (projectId: string, invoer: { kwaliteit?: 'snel' | 'kwaliteit'; hoogte?: number } = {}) =>
    verzoek<{ taakId: string; verwacht: { duurSeconden: number; breedte: number; hoogte: number; fps: number } }>(
      `/v1/projecten/${projectId}/exports`,
      { method: 'POST', json: invoer },
    ),
  exportItem: (id: string) => verzoek<ExportItem>(`/v1/exports/${id}`),
};

export type { Taak, ExportItem, Capaciteiten, ProjectSamenvatting };
